/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { find } from "lodash";

import * as ProgramProfileConstants from "./ProgramProfileConstants";

import { IFilterField } from "../Interfaces/IFilterField";
import { IKeyText } from "../Interfaces/IKeyText";
import { applicationStatusMLSA } from "./ApplicationConstants";
import { ApplicationStatusTypesEnum } from "./ApplicationStatusTypes";
import { fieldTypes, FilterValueFieldNames } from "./FilterFields";

export const defaultDelay = 1000;
export const defaultListingPageSize = 16;
export const nonMSPTenantList = ["MVP", "RD", "Reconnect"];
export const MSPTenant = "MSP";
export const studentPartnerEmailDomain = "studentambassadors.com";
export const placeholderString = "---";
export const provisionedBenefitName = "MSP Visual Studio Benefit";

/**
 * State of Countries where State selection is mandatory.
 */
export const STATE_COUNTRIES = ["Canada", "United States"];

export enum ProfileTabs {
  Profile = "profile",
  ProgramInformation = "program-information",
  Benefits = "benefits",
  Comments = "comments",
  DistributionLists = "DistributionLists",
}

export const milestones: IKeyText[] = [
  {
    key: "New",
    text: "New",
  },
  {
    key: "Alpha",
    text: "Alpha",
  },
  {
    key: "Beta",
    text: "Beta",
  },
  {
    key: "Gold",
    text: "Gold",
  },
];
export const newMilestone = find(milestones, { key: "New" })?.key;

export const programStatusTypes = {
  INPROCESS: ProgramProfileConstants.INPROCESS,
  ACTIVE: ProgramProfileConstants.ACTIVE,
  ACTIVE_VENDOR: ProgramProfileConstants.ACTIVE_VENDOR,
  DECLINED: ProgramProfileConstants.DECLINED,
  RENEWAL_DECLINED: ProgramProfileConstants.RENEWAL_DECLINED,
  AWARD_EXPIRED: ProgramProfileConstants.AWARD_EXPIRED,
  NEW_ONBOARDING: ProgramProfileConstants.NEW_ONBOARDING,
  RENEWAL_ONBOARDING: ProgramProfileConstants.RENEWAL_DECLINED,
  REMOVE_CODE_OF_CONDUCT: ProgramProfileConstants.REMOVE_CODE_OF_CONDUCT,
  REMOVE_NDA_VIOLATION: ProgramProfileConstants.REMOVE_NDA_VIOLATION,
  REMOVE_CONFLICT_OF_INTEREST: ProgramProfileConstants.REMOVE_CONFLICT_OF_INTEREST,
  REMOVE_OTHER: ProgramProfileConstants.REMOVE_OTHER,
  RETIRED_RESIGNED: ProgramProfileConstants.RETIRED_RESIGNED,
  RETIRED_GRADUATED: ProgramProfileConstants.RETIRED_GRADUATED,
  RETIRED_AWARD_RENOUNCED: ProgramProfileConstants.RETIRED_AWARD_RENOUNCED,
  RETIRED_JOINED_MICROSOFT: ProgramProfileConstants.RETIRED_JOINED_MICROSOFT,
  RETIRED_OTHER: ProgramProfileConstants.RETIRED_OTHER,
};

export const acceptedApplicationStatus = applicationStatusMLSA.filter(
  (item) => item.key === ApplicationStatusTypesEnum.INVITED
)[0].key;

export const MlsaProgramProfileStatuses: IKeyText[] = [
  {
    key: ProgramProfileConstants.INPROCESS,
    text: ProgramProfileConstants.INPROCESS,
  },
  {
    key: ProgramProfileConstants.ACTIVE,
    text: ProgramProfileConstants.ACTIVE,
  },
  {
    key: ProgramProfileConstants.DECLINED,
    text: ProgramProfileConstants.DECLINED,
  },
  {
    key: ProgramProfileConstants.RENEWAL_DECLINED,
    text: ProgramProfileConstants.RENEWAL_DECLINED,
  },
  {
    key: ProgramProfileConstants.AWARD_EXPIRED,
    text: ProgramProfileConstants.AWARD_EXPIRED,
  },
  {
    key: ProgramProfileConstants.NEW_ONBOARDING,
    text: ProgramProfileConstants.NEW_ONBOARDING,
  },
  {
    key: ProgramProfileConstants.RENEWAL_ONBOARDING,
    text: ProgramProfileConstants.RENEWAL_ONBOARDING,
  },
  {
    key: ProgramProfileConstants.REMOVE_CODE_OF_CONDUCT,
    text: ProgramProfileConstants.REMOVE_CODE_OF_CONDUCT,
  },
  {
    key: ProgramProfileConstants.REMOVE_CONFLICT_OF_INTEREST,
    text: ProgramProfileConstants.REMOVE_CONFLICT_OF_INTEREST,
  },
  {
    key: ProgramProfileConstants.REMOVE_OTHER,
    text: ProgramProfileConstants.REMOVE_OTHER,
  },
  {
    key: ProgramProfileConstants.RETIRED_JOINED_MICROSOFT,
    text: ProgramProfileConstants.RETIRED_JOINED_MICROSOFT,
  },
  {
    key: ProgramProfileConstants.RETIRED_AWARD_RENOUNCED,
    text: ProgramProfileConstants.RETIRED_AWARD_RENOUNCED,
  },
  {
    key: ProgramProfileConstants.RETIRED_GRADUATED,
    text: ProgramProfileConstants.RETIRED_GRADUATED,
  },
  {
    key: ProgramProfileConstants.RETIRED_RESIGNED,
    text: ProgramProfileConstants.RETIRED_RESIGNED,
  },
  {
    key: ProgramProfileConstants.RETIRED_OTHER,
    text: ProgramProfileConstants.RETIRED_OTHER,
  },
];
export const mvpProgramProfileStatuses: IKeyText[] = [
  {
    key: ProgramProfileConstants.INPROCESS,
    text: ProgramProfileConstants.INPROCESS,
  },
  {
    key: ProgramProfileConstants.ACTIVE,
    text: ProgramProfileConstants.ACTIVE,
  },
  {
    key: ProgramProfileConstants.ACTIVE_VENDOR,
    text: ProgramProfileConstants.ACTIVE_VENDOR,
  },
  {
    key: ProgramProfileConstants.DECLINED,
    text: ProgramProfileConstants.DECLINED,
  },
  {
    key: ProgramProfileConstants.RENEWAL_DECLINED,
    text: ProgramProfileConstants.RENEWAL_DECLINED,
  },
  {
    key: ProgramProfileConstants.AWARD_EXPIRED,
    text: ProgramProfileConstants.AWARD_EXPIRED,
  },
  {
    key: ProgramProfileConstants.NEW_ONBOARDING,
    text: ProgramProfileConstants.NEW_ONBOARDING,
  },
  {
    key: ProgramProfileConstants.RENEWAL_ONBOARDING,
    text: ProgramProfileConstants.RENEWAL_ONBOARDING,
  },
];

export const mvpProgramProfileRetireStatuses: IKeyText[] = [
  {
    key: ProgramProfileConstants.REMOVE_CODE_OF_CONDUCT,
    text: ProgramProfileConstants.REMOVE_CODE_OF_CONDUCT,
  },
  {
    key: ProgramProfileConstants.REMOVE_NDA_VIOLATION,
    text: ProgramProfileConstants.REMOVE_NDA_VIOLATION,
  },
  {
    key: ProgramProfileConstants.REMOVE_CONFLICT_OF_INTEREST,
    text: ProgramProfileConstants.REMOVE_CONFLICT_OF_INTEREST,
  },
  {
    key: ProgramProfileConstants.REMOVE_OTHER,
    text: ProgramProfileConstants.REMOVE_OTHER,
  },
  {
    key: ProgramProfileConstants.RETIRED_JOINED_MICROSOFT,
    text: ProgramProfileConstants.RETIRED_JOINED_MICROSOFT,
  },
  {
    key: ProgramProfileConstants.RETIRED_AWARD_RENOUNCED,
    text: ProgramProfileConstants.RETIRED_AWARD_RENOUNCED,
  },
  {
    key: ProgramProfileConstants.RETIRED_RESIGNED,
    text: ProgramProfileConstants.RETIRED_RESIGNED,
  },
  {
    key: ProgramProfileConstants.RETIRED_OTHER,
    text: ProgramProfileConstants.RETIRED_OTHER,
  },
  {
    key: ProgramProfileConstants.REMOVED_NON_COMPLIANT,
    text: ProgramProfileConstants.REMOVED_NON_COMPLIANT,
  },
];

export const mvpAnnualExceptionOptions: IKeyText[] = [
  {
    key: "Yes",
    text: "Yes",
  },
  {
    key: "No",
    text: "No",
  },
];

export const isModernshipOptions: IKeyText[] = [
  {
    key: "true",
    text: "Yes",
  },
  {
    key: "false",
    text: "No",
  },
];

export const AwardkitMilestone: IKeyText[] = [
  {
    key: "MIC150042",
    text: "New",
  },
  {
    key: "MIC150041",
    text: "Renew",
  },
  {
    key: "MIC150050",
    text: "5-Year Anniversary Disc",
  },
  {
    key: "MIC150045",
    text: "10-Year Anniversary Disc",
  },
  {
    key: "MIC150046",
    text: "15-Year Anniversary Disc",
  },
  {
    key: "MIC150047",
    text: "20-Year Anniversary Disc",
  },
  {
    key: "MIC150048",
    text: "25-Year Anniversary Disc",
  },
  {
    key: "MIC150049",
    text: "30-Year Anniversary Disc",
  },
  {
    key: "MIC150051",
    text: "35-Year Anniversary Disc",
  },
];

//TODO: this will be removed when the endpoint for admin common data is ready.
export const rdProgramProfileStatuses: IKeyText[] = [
  {
    key: ProgramProfileConstants.INPROCESS,
    text: ProgramProfileConstants.INPROCESS,
  },
  {
    key: ProgramProfileConstants.ACTIVE,
    text: ProgramProfileConstants.ACTIVE,
  },
  {
    key: ProgramProfileConstants.ACTIVE_VENDOR,
    text: ProgramProfileConstants.ACTIVE_VENDOR,
  },
  {
    key: ProgramProfileConstants.DECLINED,
    text: ProgramProfileConstants.DECLINED,
  },
  {
    key: ProgramProfileConstants.RENEWAL_DECLINED,
    text: ProgramProfileConstants.RENEWAL_DECLINED,
  },
  {
    key: ProgramProfileConstants.AWARD_EXPIRED,
    text: ProgramProfileConstants.AWARD_EXPIRED,
  },
  {
    key: ProgramProfileConstants.NEW_ONBOARDING,
    text: ProgramProfileConstants.NEW_ONBOARDING,
  },
  {
    key: ProgramProfileConstants.RENEWAL_ONBOARDING,
    text: ProgramProfileConstants.RENEWAL_ONBOARDING,
  },
  {
    key: ProgramProfileConstants.REMOVE_CODE_OF_CONDUCT,
    text: ProgramProfileConstants.REMOVE_CODE_OF_CONDUCT,
  },
  {
    key: ProgramProfileConstants.REMOVE_NDA_VIOLATION,
    text: ProgramProfileConstants.REMOVE_NDA_VIOLATION,
  },
  {
    key: ProgramProfileConstants.REMOVE_CONFLICT_OF_INTEREST,
    text: ProgramProfileConstants.REMOVE_CONFLICT_OF_INTEREST,
  },
  {
    key: ProgramProfileConstants.REMOVE_OTHER,
    text: ProgramProfileConstants.REMOVE_OTHER,
  },
  {
    key: ProgramProfileConstants.RETIRED_JOINED_MICROSOFT,
    text: ProgramProfileConstants.RETIRED_JOINED_MICROSOFT,
  },
  {
    key: ProgramProfileConstants.RETIRED_AWARD_RENOUNCED,
    text: ProgramProfileConstants.RETIRED_AWARD_RENOUNCED,
  },
  {
    key: ProgramProfileConstants.RETIRED_RESIGNED,
    text: ProgramProfileConstants.RETIRED_RESIGNED,
  },
  {
    key: ProgramProfileConstants.RETIRED_OTHER,
    text: ProgramProfileConstants.RETIRED_OTHER,
  },
  {
    key: ProgramProfileConstants.REMOVED_NON_COMPLIANT,
    text: ProgramProfileConstants.REMOVED_NON_COMPLIANT,
  },
];

export const activeProgramProfileStatus = MlsaProgramProfileStatuses.filter(
  (item) => item.key === "Active"
)[0].key;

export const renewalStatuses: IKeyText[] = [
  {
    key: "UnderReview",
    text: "Under Review",
  },
  {
    key: "RenewalInvited",
    text: "Renewal Invited",
  },
  {
    key: "RenewalRejected",
    text: "Renewal Rejected",
  },
  {
    key: "RenewalAccepted",
    text: "Renewal Accepted",
  },
];

export const schoolDegreeLevels: IKeyText[] = [
  { key: "Bachelor", text: "Bachelor" },
  { key: "Master", text: "Master" },
  { key: "PhD", text: "PhD" },
];

export const schoolDegreePrograms: IKeyText[] = [
  { key: "Computer Science", text: "Computer Science" },
  { key: "Electrical Engineering", text: "Electrical Engineering" },
  { key: "Software Engineering", text: "Software Engineering" },
  { key: "Systems Engineering", text: "Systems Engineering" },
  { key: "Engineering - Other", text: "Engineering - Other" },
  { key: "Information Systems", text: "Information Systems" },
  { key: "Cyber Security", text: "Cyber Security" },
  { key: "Business", text: "Business" },
  { key: "Other", text: "Other" },
];

export const genders: IKeyText[] = [
  { key: "Woman", text: "Woman" },
  { key: "Man", text: "Man" },
  { key: "Non-Binary", text: "Non-Binary" },
  { key: "Choose not to answer", text: "Choose not to answer" },
];

export const pronounPreferences: IKeyText[] = [
  { key: "He/Him/His", text: "He/Him/His" },
  { key: "She/Her/Hers", text: "She/Her/Hers" },
  { key: "They/Them/Theirs", text: "They/Them/Theirs" },
  { key: "Choose not to answer", text: "Choose not to answer" },
];

export const titles: IKeyText[] = [
  { key: "Mr.", text: "Mr." },
  { key: "Mrs.", text: "Mrs." },
  { key: "Miss", text: "Miss" },
  { key: "Ms.", text: "Ms." },
  { key: "Mx", text: "Mx" },
  { key: "Dr.", text: "Dr." },
  { key: "Choose not to answer", text: "Choose not to answer" },
];

export const shirtFitTypes: IKeyText[] = [
  { key: "Man", text: "Man" },
  { key: "Woman", text: "Woman" },
];

export const shirtSizes: IKeyText[] = [
  { key: "XS", text: "XS" },
  { key: "S", text: "S" },
  { key: "M", text: "M" },
  { key: "L", text: "L" },
  { key: "XL", text: "XL" },
  { key: "XXL", text: "XXL" },
  { key: "XXXL", text: "XXXL" },
];

export const ndaStatus: IKeyText[] = [
  { key: "Compliant", text: "Compliant" },
  { key: "Non-compliant", text: "Non-compliant" },
];

export const namesLocalized: { key: boolean; text: string }[] = [
  { key: true, text: "Localized" },
  { key: false, text: "English" },
];

export const urlPrefix = "https://";

/**
 * Dropdown options for the provisioned selector
 */
export const provisionedOptions: IKeyText[] = [
  {
    key: "true",
    text: "Yes",
  },
  {
    key: "false",
    text: "No",
  },
];

export const studentOtherProgramMembershipList: IKeyText[] = [
  {
    key: "MVP",
    text: "MVP",
  },
  {
    key: "RD",
    text: "RD",
  },
];

export const mvpOtherProgramMembershipList: IKeyText[] = [
  {
    key: "MSP",
    text: "MLSA",
  },
  {
    key: "RD",
    text: "RD",
  },
];

export const rdOtherProgramMembershipList: IKeyText[] = [
  {
    key: "MSP",
    text: "MLSA",
  },
  {
    key: "MVP",
    text: "MVP",
  },
];

export const ovdfBlockOptions: IKeyText[] = [
  {
    key: "true",
    text: "Yes",
  },
  {
    key: "false",
    text: "No",
  },
];

/**
 * Setting of constants for defualt field strings
 * we use to set in reducer.
 */
export const APPLICATION_STATUS = "applicationStatus";
export const PROGRAM_STATUS = "programStatus";
export const AVAILABLE_CPM_LIST = "availableCpmList";

/**
 * Sidebar filter constants used for rendering
 */
export const mlsaUserSidebarFilterFields: IFilterField[] = [
  {
    key: APPLICATION_STATUS,
    text: "Application Status",
    type: fieldTypes.MULTI_SELECT,
    options: applicationStatusMLSA,
  },
  {
    key: PROGRAM_STATUS,
    text: "Program Status",
    type: fieldTypes.MULTI_SELECT,
    options: MlsaProgramProfileStatuses,
  },
  {
    key: "milestone",
    text: "Milestone",
    type: fieldTypes.MULTI_SELECT,
    options: milestones,
  },
  {
    key: "provisioned",
    text: "Benefits Provisioned?",
    type: fieldTypes.MULTI_SELECT,
    options: provisionedOptions,
  },
  {
    key: FilterValueFieldNames.AVAILABLE_CPM_LIST,
    text: "Cpm",
    type: fieldTypes.AVAILABLE_CPM_LIST,
  },
  {
    key: "country",
    text: "Country/Region",
    type: fieldTypes.COUNTRY,
  },
  {
    key: "regionState",
    text: "State/Province",
    type: fieldTypes.PROVINCE,
  },
  {
    key: FilterValueFieldNames.OTHER_PROGRAM_MEMBERSHIP,
    text: "Other Program Membership",
    type: fieldTypes.MULTI_SELECT,
    options: studentOtherProgramMembershipList,
  },
  {
    key: "programStartDate",
    text: "Program Start Date",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: "programEndDate",
    text: "Program End Date",
    type: fieldTypes.DATE_PICKER,
  },
];

/**
 * Default filter values for MLSA
 */
export const defaultMlsaFilterValues: {
  fieldName: string;
  value: string;
}[] = [
  { fieldName: APPLICATION_STATUS, value: acceptedApplicationStatus },
  { fieldName: PROGRAM_STATUS, value: activeProgramProfileStatus },
];

/**
 * Sidebar filter constants used for rendering
 */
export const mvpSidebarFilterFields: IFilterField[] = [
  {
    key: FilterValueFieldNames.PROGRAM_STATUS,
    text: "Program Status",
    type: fieldTypes.MULTI_SELECT,
    options: mvpProgramProfileStatuses,
  },
  {
    key: FilterValueFieldNames.COUNTRY,
    text: "Country",
    type: fieldTypes.COUNTRY,
  },
  {
    key: FilterValueFieldNames.REGION,
    text: "Region",
    type: fieldTypes.CL_REGION,
  },
  {
    key: FilterValueFieldNames.STATE_PROVINCE,
    text: "State/Province",
    type: fieldTypes.PROVINCE,
  },
  {
    key: FilterValueFieldNames.GRANT_ANNUAL_EXCEPTION,
    text: "Grant Annual Exception",
    type: fieldTypes.MULTI_SELECT,
    options: mvpAnnualExceptionOptions,
  },
  {
    key: FilterValueFieldNames.TECHNOLOGY_AREA_GROUP,
    text: "Award Category",
    type: fieldTypes.TECHNOLOGY_AREA_GROUP,
  },
  {
    key: FilterValueFieldNames.TECHNOLOGY_AREA,
    text: "Technology Area",
    type: fieldTypes.TECHNOLOGY_AREA,
  },
  {
    key: FilterValueFieldNames.OTHER_PROGRAM_MEMBERSHIP,
    text: "Other Program Membership",
    type: fieldTypes.MULTI_SELECT,
    options: mvpOtherProgramMembershipList,
  },
  {
    key: FilterValueFieldNames.LAST_INVITED_START_DATE,
    text: "Last Invited Start Date",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: FilterValueFieldNames.LAST_INVITED_END_DATE,
    text: "Last Invited End Date",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: FilterValueFieldNames.NDA_STATUS,
    text: "NDA Status",
    type: fieldTypes.MULTI_SELECT,
    options: ndaStatus,
  },
  {
    key: FilterValueFieldNames.AVAILABLE_CPM_LIST,
    text: "CPMs",
    type: fieldTypes.AVAILABLE_CPM_LIST,
  },
  {
    key: FilterValueFieldNames.OVDF_BLOCK,
    text: "OVDF Block",
    type: fieldTypes.SELECT,
    options: ovdfBlockOptions,
  },
];

/**
 * Default filter values for MVP
 */
export const defaultMvpFilterValues: {
  fieldName: string;
  value: string;
}[] = [];

/**
 * Sidebar filter constants used for rendering
 */
export const rdSidebarFilterFields: IFilterField[] = [
  {
    key: PROGRAM_STATUS,
    text: "Program Status",
    type: fieldTypes.MULTI_SELECT,
    options: rdProgramProfileStatuses,
  },
  {
    key: "industryFocus",
    text: "Industry Focus",
    type: fieldTypes.INDUSTRY_FOCUS,
  },
  {
    key: "technicalExpertise",
    text: "Technical Expertise",
    type: fieldTypes.TECHNICAL_EXPERTISE,
  },
  {
    key: "clRegion",
    text: "Region",
    type: fieldTypes.CL_REGION,
  },
  {
    key: "country",
    text: "Country",
    type: fieldTypes.COUNTRY,
  },
  {
    key: "stateOrProvince",
    text: "State/Province",
    type: fieldTypes.PROVINCE,
  },
  {
    key: FilterValueFieldNames.OTHER_PROGRAM_MEMBERSHIP,
    text: "Other Program Membership",
    type: fieldTypes.MULTI_SELECT,
    options: rdOtherProgramMembershipList,
  },
  {
    key: FilterValueFieldNames.AWARD_PERIOD_START_DATE,
    text: "Award Period Start Date",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: FilterValueFieldNames.AWARD_PERIOD_END_DATE,
    text: "Award Period End Date",
    type: fieldTypes.DATE_PICKER,
  },
];

/**
 * Default filter values for RD
 */
export const defaultRdFilterValues: {
  fieldName: string;
  value: string;
}[] = [
  {
    fieldName: "programStatus",
    value: "Active",
  },
];

export enum mvpProgramStatusTypes {
  ACTIVE = "Active",
  COMPLETED = "Completed",
  DENIED = "Denied",
  FUTURE = "Future",
  REMOVED = "Removed",
  RESIGNED = "Resigned",
  RETIRED = "Retired",
  SAVED = "Saved",
}

export const mvpProgramProfileStrengths: IKeyText[] = [
  {
    key: "Mentor",
    text: "Mentor",
  },
  {
    key: "Community Organizer",
    text: "Community Organizer",
  },
  {
    key: "Social media influencer",
    text: "Social Media Influencer",
  },
  {
    key: "Public Speaker",
    text: "Public Speaker",
  },
  {
    key: "Content Creator",
    text: "Content Creator",
  },
  {
    key: "Helper",
    text: "Helper",
  },
  {
    key: "Open Source and All Code Project Supporter",
    text: "Open Source and All Code Project Supporter",
  },
];

/**
 * the "missing" countries list.
 */
export const VALIDATE_COUNTRY_NAMES = [
  "Armenia",
  "United Arab Emirates",
  "Bahrain",
  "Algeria",
  "Egypt",
  "Iraq",
  "Jordan",
  "Kuwait",
  "Saudi Arabia",
  "Tunisia",
  "Azerbaijan",
  "Bosnia and Herzegovina",
  "Belarus",
  "Bulgaria",
  "Bangladesh",
  "Czechia",
  "Denmark",
  "Germany",
  "Austria",
  "Switzerland",
  "Greece",
  "Australia",
  "Canada",
  "United Kingdom",
  "Guernsey",
  "Ghana",
  "Hong Kong SAR",
  "Macao SAR",
  "Ireland",
  "India",
  "Kenya",
  "Malta",
  "Mauritius",
  "Nigeria",
  "New Zealand",
  "Pakistan",
  "Philippines",
  "Rwanda",
  "Singapore",
  "Sierra Leone",
  "Uganda",
  "United States",
  "Tanzania",
  "South Africa",
  "Zambia",
  "Zimbabwe",
  "Argentina",
  "Chile",
  "Colombia",
  "Costa Rica",
  "Ecuador",
  "Spain",
  "Mexico",
  "Panama",
  "Paraguay",
  "Uruguay",
  "Estonia",
  "Finland",
  "Fiji",
  "Faroe Islands",
  "Belgium",
  "France",
  "French Guiana",
  "Guadeloupe",
  "Martinique",
  "Réunion",
  "Israel",
  "Hungary",
  "Indonesia",
  "Italy",
  "Japan",
  "Kazakhstan",
  "Greenland",
  "Korea",
  "Lithuania",
  "Malaysia",
  "Norway",
  "Nepal",
  "Netherlands",
  "Norway",
  "Poland",
  "Brazil",
  "Portugal",
  "Romania",
  "Russia",
  "Slovenia",
  "Slovakia",
  "Serbia",
  "Sweden",
  "Thailand",
  "Turkey",
  "Ukraine",
  "Vietnam",
  "China",
  "Hong Kong SAR",
  "Macao SAR",
  "Taiwan",
  "Uzbekistan",
  "Georgia",
  "Bolivia",
  "Peru",
  "Lebanon",
];

/**
 * Default values for Registration & Program Status for Student Ambassador
 */
export const MLSA_APPLICATION_STATUS_TEMPLATE = "MSPUserProfileKey, ApplicationStatus";
export const MLSA_PROGRAM_STATUS_TEMPLATE = "MSPUserProfileKey, ProgramStatus";
export const ALLOWED_FILE_SIZE_MB = 0.1; // 100 KB in MB
export const VALID_FILE_TYPES = ["csv"];
export const INVALID_FILE = "Invalid File";
export const INVALID_FILE_SIZE = "Invalid File Size";

export const SwagKitType: IKeyText[] = [
  {
    key: "SwagKit - Beta",
    text: "Beta Swag Kit",
  },
  {
    key: "SwagKit - Gold",
    text: "Gold Swag Kit",
  },
];
